<template>
  <b-sidebar
    id="sidebar-create-request-letter"
    sidebar-class="sidebar-lg"
    bg-variant="white"
    shadow
    backdrop
    no-header
    right
  >
    <template #default="{ hide }">
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          {{ requestLetter.id ? $t('Update') : $t('Create') }}
        </h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="clear(hide)"
        />
      </div>

      <b-form
        class="p-2"
        @submit.prevent
      >
        <validation-observer ref="requestLetterCreate">
          <b-form-group
            :label="$t('Title')"
            label-for="request-letter-title"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Title')"
              rules="required"
            >
              <b-form-input
                id="request-letter-title"
                v-model="requestLetter.title"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('Body')"
            label-for="request-letter-body"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Body')"
              rules="required"
            >
              <b-form-textarea
                id="request-letter-body"
                v-model="requestLetter.body"
                rows="5"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group :label="$t('Apply Documents')">
            <div class="d-flex align-items-center">
              <b-button
                v-b-modal.request-letter-students-modal
                variant="outline-primary"
                class="mr-1"
              >
                <feather-icon
                  icon="ListIcon"
                  class="mr-50"
                />
                <span>{{ $t('Select') }}</span>
              </b-button>
              <span class="text-muted">
                {{ $t('Selected') }}: {{ selectedCount }}
              </span>
            </div>
          </b-form-group>

          <b-form-group
            :label="$t('File')"
            label-for="request-letter-file"
          >
            <b-form-file
              id="request-letter-file"
              v-model="requestLetter.attachment_file"
              accept="application/pdf"
              :placeholder="$t('Choose a file')"
              :browse-text="$t('Browse')"
            />
            <small
              v-if="typeof requestLetter.attachment_file === 'string'"
              class="text-muted d-block mt-50"
            >
              {{ $t('Current file is saved') }}
            </small>
          </b-form-group>
        </validation-observer>

        <div class="d-flex mt-2">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :variant="requestLetter.id ? 'primary' : 'success'"
            class="mr-2"
            type="submit"
            @click="submit(hide)"
          >
            {{ requestLetter.id ? $t('Update') : $t('Create') }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="clear(hide)"
          >
            {{ $t('Cancel') }}
          </b-button>
        </div>
      </b-form>

      <b-modal
        id="request-letter-students-modal"
        size="xl"
        :title="$t('Apply Documents')"
        @show="openStudentsModal"
      >
        <div class="d-flex justify-content-end mb-1">
          <span class="text-muted">
            {{ $t('Selected') }}: {{ pendingSelectedCount }}
          </span>
        </div>
        <l-table
          ref="orgStudentsTable"
          :module-name="ORG_STUDENTS_MODULE_NAME"
          :table-columns="orgStudentColumns"
          :filter-class="filterClass"
          :table-config-options="{ endpoint: `${MODULE_NAME}/orgStudents`, syncRoute: false, perPage: 5 }"
          :table-props="orgStudentsTableProps"
          :table-listeners="orgStudentsTableListeners"
        >
          <template #cell(selected)="{ data }">
            <b-form-checkbox
              :checked="isPendingSelected(data.item)"
              @click.native.stop
              @change="toggleStudent(data.item, $event)"
            />
          </template>

          <template #cell(user)="{ data }">
            {{ data.value.full_name }}
          </template>

          <template #cell(dc_type)="{ data }">
            {{ $langName(getDcType(data.value).name) }}
          </template>

          <template #cell(direction)="{ data }">
            {{ data.value ? `${data.value.code} - ${$langName(data.value.name)}` : '-' }}
          </template>

          <template #cell(status)="{ data }">
            <b-badge :variant="getApplyDocumentStatusColor(data.value)">
              {{ $langName(getApplyDocumentStatus(data.value).name) }}
            </b-badge>
          </template>
        </l-table>

        <template #modal-footer="{ cancel }">
          <b-button
            variant="outline-secondary"
            @click="cancel()"
          >
            {{ $t('Cancel') }}
          </b-button>
          <b-button
            variant="primary"
            @click="applySelectedStudents"
          >
            {{ $t('Select') }}
          </b-button>
        </template>
      </b-modal>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BButton, BFormTextarea, BFormInput, BFormFile, BModal, VBModal, BFormCheckbox, BBadge,
} from 'bootstrap-vue'
import { onUnmounted } from '@vue/composition-api'
import Ripple from 'vue-ripple-directive'
import { ValidationProvider, ValidationObserver, localize } from 'vee-validate'
import { required } from '@/libs/validations/validations'
import store from '@/store'
import periodModule from '@/store/period'
import LTable from '@/views/components/LTable.vue'
import StudentList from '@/views/components/filters/service/StudentList'
import { myOrganization } from '@/auth/utils'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BSidebar,
    BForm,
    BFormGroup,
    BButton,
    BFormTextarea,
    BFormInput,
    BFormFile,
    BModal,
    BFormCheckbox,
    BBadge,
    LTable,
  },
  directives: {
    Ripple,
    'b-modal': VBModal,
  },
  data() {
    return {
      required,
      filterClass: new StudentList(),
      pendingApplyDocs: [],
      orgStudentColumns: [
        { key: 'selected', label: '' },
        { key: 'user', label: 'Student', sortable: true },
        { key: 'dc_type', label: 'Dc Type', sortable: true },
        { key: 'direction', label: 'Direction', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
      ],
      orgStudentsTableProps: {
        tbodyTrClass: 'cursor-pointer',
      },
      orgStudentsTableListeners: {
        'row-clicked': this.toggleStudentByRow,
      },
    }
  },
  computed: {
    requestLetter: {
      get() {
        return this.$store.state.requestLetters.requestLetter
      },
      set(data) {
        this.$store.commit('requestLetters/UPDATE', data)
      },
    },
    selectedCount() {
      return (this.requestLetter.apply_docs || []).length
    },
    pendingSelectedCount() {
      return this.pendingApplyDocs.length
    },
  },
  watch: {
    selectedCount() {
      this.pendingApplyDocs = [...(this.requestLetter.apply_docs || [])]
    },
  },
  created() {
    localize(this.$i18n.locale)
    this.loadFilterData()
  },
  methods: {
    loadFilterData() {
      this.$store.dispatch('staticModule/dcType')
      this.$store.dispatch('staticModule/applyDocumentStatuses')
      this.$store.dispatch('period/list', { limit: this.MAX_LIMIT, ordering: '-admission_year' })
      const organization = myOrganization()
      const orgId = organization?.id || organization
      if (orgId) {
        this.$store.dispatch('staticModule/directionList', { org_id: orgId })
      }
    },
    getDcType(id) {
      return this.$store.getters['staticModule/getDcType'](id) || { name: {} }
    },
    getApplyDocumentStatus(id) {
      return this.$store.getters['staticModule/getApplyDocumentStatus'](id) || { name: {} }
    },
    getApplyDocumentStatusColor(id) {
      return this.$store.getters['staticModule/getApplyDocumentStatusColor'](id)
    },
    openStudentsModal() {
      this.pendingApplyDocs = [...(this.requestLetter.apply_docs || [])]
    },
    isPendingSelected(student) {
      return this.pendingApplyDocs.includes(student.id)
    },
    toggleStudent(student, checked) {
      const applyDocs = new Set(this.pendingApplyDocs)
      if (checked) {
        applyDocs.add(student.id)
      } else {
        applyDocs.delete(student.id)
      }
      this.pendingApplyDocs = [...applyDocs]
    },
    toggleStudentByRow(student) {
      this.toggleStudent(student, !this.isPendingSelected(student))
    },
    applySelectedStudents() {
      this.requestLetter = {
        ...this.requestLetter,
        apply_docs: [...this.pendingApplyDocs],
      }
      this.$bvModal.hide('request-letter-students-modal')
    },
    clear(hide) {
      this.$store.commit('requestLetters/FRESH')
      this.$refs.requestLetterCreate.reset()
      hide()
    },
    submit(hide) {
      this.$refs.requestLetterCreate.validate().then(success => {
        if (!success) return

        this.$createSwal(
          this,
          this.requestLetter,
          `requestLetters/${this.requestLetter.id ? 'update' : 'create'}`,
          () => {
            this.$emit('refetchData')
            this.$store.commit('requestLetters/FRESH')
            hide()
          },
        )
      })
    },
  },
  setup() {
    const MODULE_NAME = 'requestLetters'
    const ORG_STUDENTS_MODULE_NAME = 'requestLetterOrgStudents'
    const hasPeriodModule = store.hasModule('period')
    if (!hasPeriodModule) store.registerModule('period', periodModule)

    onUnmounted(() => {
      if (!hasPeriodModule && store.hasModule('period')) store.unregisterModule('period')
    })

    return {
      MODULE_NAME,
      ORG_STUDENTS_MODULE_NAME,
    }
  },
}
</script>
